/** Iter 639 — staff till split · ONE live session row: tappable header,
 * expanded detail (amounts / timeline), bill set/✏️ edit, cancel.
 * Moved verbatim from pages/TillPage.jsx. */
import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { tillApi, detail, money } from "@/features/staffTill/helpers";

/** Iter-546 — expanded session detail from GET /till/sessions/{id}. */
function TillSessionDetail({ id }) {
  const qc = useQueryClient();
  // Iter 685 — result line after a till approve/reject.
  const [decisionMsg, setDecisionMsg] = useState(null);
  const q = useQuery({
    queryKey: ["till-session-detail", id],
    queryFn: async () => (await tillApi.get(`/classifieds/engagement/till/sessions/${id}`)).data,
  });
  // Iter 685 — staff with `approve_reviews` decide the customer's post
  // right from the till (same engine as the owner dashboard / one-tap).
  const decideMut = useMutation({
    mutationFn: async (approve) =>
      (await tillApi.post(`/classifieds/engagement/till/sessions/${id}/review/decision`, { approve })).data,
    onSuccess: (data, approve) => {
      setDecisionMsg(
        data?.message ||
          (approve
            ? data?.points_credited != null
              ? `✓ Approved — ${data.points_credited} points credited`
              : "✓ Approved — customer's discount unlocked"
            : "✕ Rejected — the customer has been told"),
      );
      q.refetch();
      qc.invalidateQueries({ queryKey: ["till-sessions"] });
    },
    onError: (err) => toast.error(detail(err, "Couldn't record the decision")),
  });
  if (q.isLoading) {
    return <div className="mt-3 text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /></div>;
  }
  const d = q.data;
  if (!d) return <div className="mt-3 text-xs text-slate-400">Couldn&apos;t load details.</div>;
  const a = d.amounts || {};
  return (
    <div className="mt-3 rounded-xl bg-slate-50 border border-slate-200 p-4 text-xs text-slate-600 space-y-1.5" data-testid={`till-session-detail-${id}`}>
      {d.friendly_status && <div className="font-bold text-slate-800">{d.friendly_status}</div>}
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {a.display_bill_amount != null && <span>Bill {money(a.display_bill_amount)}</span>}
        {a.discount_amount != null && <span>Discount −{money(a.discount_amount)}</span>}
        {(a.service_fee_total ?? a.fee_amount) != null && (
          <span>Service fee {money(a.service_fee_total ?? a.fee_amount)}</span>
        )}
        {a.charge_total != null && <span className="font-bold text-slate-900">Total {money(a.charge_total)}</span>}
      </div>
      {d.paid_at && <div>Paid {new Date(d.paid_at).toLocaleString("en-NZ")}</div>}
      {d.review_hours_left != null && <div>Review window: {d.review_hours_left} h left</div>}
      {/* Iter 685 — review box: status, notification receipts, and
          ✓ Approve / ✕ Reject when this staff holds `approve_reviews`. */}
      {d.review && (
        <div className="pt-2 mt-1 border-t border-slate-200 space-y-1.5" data-testid={`till-review-box-${id}`}>
          <div className="font-bold text-slate-800">
            Customer post{d.review.platform ? ` · ${d.review.platform}` : ""} —{" "}
            {d.review.decision === "rejected"
              ? "✗ rejected"
              : d.review.verified || d.review.decision === "approved" || d.review.decision === "auto_verified"
              ? "✅ verified"
              : "⏳ awaiting approval"}
          </div>
          {d.review.post_url && (
            <a
              href={d.review.post_url}
              target="_blank"
              rel="noreferrer"
              className="inline-block text-slate-500 underline hover:text-slate-800"
              data-testid={`till-review-open-${id}`}
            >
              Open post ↗
            </a>
          )}
          {d.review.notify_channels && (
            <div data-testid={`till-review-receipts-${id}`}>
              Owner notified
              {d.review.notified_at
                ? ` ${new Date(d.review.notified_at).toLocaleTimeString("en-NZ", { hour: "2-digit", minute: "2-digit" })}`
                : ""}
              : SMS {d.review.notify_channels.sms ? "sent ✅" : "not sent ❌"} · Email{" "}
              {d.review.notify_channels.email ? "sent ✅" : "not sent ❌"}
            </div>
          )}
          {decisionMsg && (
            <div className="font-bold text-slate-900" data-testid={`till-review-decided-${id}`}>
              {decisionMsg}
            </div>
          )}
          {d.review.can_decide && !decisionMsg && (
            <div className="flex items-center gap-2 pt-0.5">
              <button
                type="button"
                data-testid={`till-review-approve-${id}`}
                disabled={decideMut.isPending}
                onClick={() => decideMut.mutate(true)}
                className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-4 py-2 transition-colors disabled:opacity-60"
              >
                {decideMut.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                ✓ Approve
              </button>
              <button
                type="button"
                data-testid={`till-review-reject-${id}`}
                disabled={decideMut.isPending}
                onClick={() => decideMut.mutate(false)}
                className="inline-flex items-center gap-1.5 rounded-full bg-red-600 hover:bg-red-700 text-white text-xs font-bold px-4 py-2 transition-colors disabled:opacity-60"
              >
                ✕ Reject
              </button>
            </div>
          )}
        </div>
      )}
      {Array.isArray(d.timeline) && d.timeline.length > 0 && (
        <ul className="pt-1 space-y-1">
          {d.timeline.map((t, i) => (
            <li key={i} className="flex gap-2">
              <span className="text-slate-400">{t.at ? new Date(t.at).toLocaleTimeString("en-NZ") : ""}</span>
              <span>{t.label || t.event || String(t)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function SessionRow({ session: s }) {
  const qc = useQueryClient();
  const [amount, setAmount] = useState("");
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [openDetail, setOpenDetail] = useState(false);
  // Iter-614 — staff can correct a mistyped bill (open/needs_review/verified).
  const [editing, setEditing] = useState(false);

  const billMut = useMutation({
    mutationFn: async () =>
      (await tillApi.put(`/classifieds/engagement/till/sessions/${s.id}/bill`, { bill_amount: parseFloat(amount) })).data,
    onSuccess: () => {
      toast.success(editing ? "Bill updated — the customer confirms the new total." : "Bill sent to the customer.");
      setAmount("");
      setEditing(false);
      qc.invalidateQueries({ queryKey: ["till-sessions"] });
    },
    onError: (err) => toast.error(detail(err, "Couldn't set the bill")),
  });

  // Iter-442+ — staff can cancel a whole UNPAID session (voids any
  // pending tap intent server-side; idempotent; 409 if already paid).
  const cancelMut = useMutation({
    mutationFn: async () =>
      (await tillApi.post(`/classifieds/engagement/till/sessions/${s.id}/cancel`, {})).data,
    onSuccess: () => {
      toast.success("Session cancelled.");
      qc.invalidateQueries({ queryKey: ["till-sessions"] });
    },
    onError: (err) => toast.error(detail(err, "Couldn't cancel the session")),
  });

  const amounts = s.amounts && typeof s.amounts === "object" ? s.amounts : null;
  const isPaid = /paid|resolved/.test(s.status || "");

  return (
    <li className="rounded-2xl bg-white shadow-card p-5" data-testid={`till-session-${s.id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <span className="font-display text-2xl font-black tracking-widest text-slate-900">{s.short_code || "—"}</span>
        {s.customer_name && <span className="text-sm text-slate-500">{s.customer_name}</span>}
        {s.unclaimed && (
          <span
            className="inline-flex items-center gap-1 rounded-full bg-sky-50 border border-sky-200 text-sky-700 text-[11px] font-bold px-2.5 py-0.5"
            data-testid={`till-session-unclaimed-${s.id}`}
          >
            📱 Waiting for customer to scan
          </span>
        )}
        {isPaid && (
          <span className="inline-flex items-center rounded-full bg-emerald-600 text-white text-[11px] font-black px-2.5 py-0.5" data-testid={`till-session-paid-${s.id}`}>
            PAID
          </span>
        )}
        {/* Iter-621 — customer chose the no-review points path. */}
        {s.review_state === "points_only" && !isPaid && (
          <span
            className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-[11px] font-bold px-2.5 py-0.5"
            data-testid={`till-session-points-only-${s.id}`}
          >
            ⭐ paying with points (5% off, no review)
          </span>
        )}
        <button
          type="button"
          data-testid={`till-session-detail-toggle-${s.id}`}
          onClick={() => setOpenDetail((v) => !v)}
          className="ml-auto text-xs font-bold uppercase tracking-widest text-slate-400 hover:text-slate-700 transition-colors"
        >
          {s.friendly_status || (s.status || "").replace(/_/g, " ")} {openDetail ? "▴" : "▾"}
        </button>
      </div>
      {/* Iter-546 — tappable detail: amounts, paid_at, review window, timeline */}
      {openDetail && <TillSessionDetail id={s.id} />}
      {/* Iter 658 — pre-computed bill corrections under the sale line. */}
      {Array.isArray(s.bill_corrections) &&
        s.bill_corrections.map((c, i) => (
          <div
            key={i}
            data-testid={`till-corr-${s.id}-${i}`}
            className="mt-2 text-xs font-semibold text-amber-700"
          >
            ✏️ Corrected by {c.by} {money(c.old)} → {money(c.new)}
            {c.at ? ` · ${new Date(c.at).toLocaleTimeString("en-NZ", { hour: "2-digit", minute: "2-digit" })}` : ""}
          </div>
        ))}
      {s.bill_amount == null || editing ? (
        <form
          className="mt-3 flex items-center gap-2 flex-wrap"
          onSubmit={(e) => {
            e.preventDefault();
            const v = parseFloat(amount);
            if (!Number.isFinite(v) || v <= 0) return toast.error("Enter the bill amount.");
            billMut.mutate();
          }}
        >
          <div className="relative flex-1 min-w-[140px]">
            <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-slate-400">$</span>
            <input value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ""))} placeholder="0.00"
              inputMode="decimal" data-testid={`till-bill-input-${s.id}`}
              className="w-full rounded-full border border-slate-300 pl-7 pr-4 py-2.5 text-sm focus:outline-none focus:border-slate-500" />
          </div>
          <button type="submit" disabled={billMut.isPending} data-testid={`till-bill-submit-${s.id}`}
            className="rounded-full bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold px-5 py-2.5 disabled:opacity-60 transition-colors">
            {billMut.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : editing ? "Update" : "Set bill"}
          </button>
          {editing && (
            <>
              <button
                type="button"
                data-testid={`till-bill-edit-cancel-${s.id}`}
                onClick={() => { setEditing(false); setAmount(""); }}
                className="rounded-full border border-slate-300 text-slate-600 hover:border-slate-500 text-xs font-bold px-4 py-2.5 transition-colors"
              >
                Cancel
              </button>
              <p className="w-full text-[11px] text-slate-500">
                The customer sees the corrected amount on their phone straight away and confirms the new total.
              </p>
            </>
          )}
        </form>
      ) : (
        <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-sm text-slate-600" data-testid={`till-bill-set-${s.id}`}>
          <span>Bill <span className="font-semibold text-slate-900">{money(s.bill_amount)}</span></span>
          {amounts?.discount != null && <span>Discount <span className="font-semibold text-emerald-600">−{money(amounts.discount)}</span></span>}
          {!isPaid && (amounts?.total ?? s.charge_total) != null && <span>To pay <span className="font-black text-slate-900">{money(amounts?.total ?? s.charge_total)}</span></span>}
          {/* Iter-614 — correct a mistyped bill (server allows open/needs_review/verified) */}
          {!isPaid && ["open", "needs_review", "verified"].includes(s.status) && (
            <button
              type="button"
              data-testid={`till-bill-edit-${s.id}`}
              onClick={() => { setEditing(true); setAmount(String(s.bill_amount)); }}
              className="inline-flex items-center gap-1 rounded-full border border-slate-300 hover:border-slate-500 text-slate-700 text-xs font-bold px-3.5 py-1.5 transition-colors"
            >
              ✏️ Edit bill ({money(s.bill_amount)})
            </button>
          )}
        </div>
      )}
      {!isPaid && s.can_cancel !== false && (
        <div className="mt-3 flex items-center gap-2">
          {confirmCancel ? (
            <>
              <span className="text-xs text-slate-500">Cancel this session? The customer will be asked to re-scan.</span>
              <button
                type="button"
                data-testid={`till-cancel-confirm-${s.id}`}
                disabled={cancelMut.isPending}
                onClick={() => cancelMut.mutate()}
                className="inline-flex items-center gap-1.5 rounded-full bg-red-600 hover:bg-red-700 text-white text-xs font-bold px-4 py-1.5 transition-colors disabled:opacity-60"
              >
                {cancelMut.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Yes, cancel
              </button>
              <button
                type="button"
                data-testid={`till-cancel-keep-${s.id}`}
                onClick={() => setConfirmCancel(false)}
                className="text-xs font-bold text-slate-500 hover:text-slate-800 underline"
              >
                Keep it
              </button>
            </>
          ) : (
            <button
              type="button"
              data-testid={`till-cancel-btn-${s.id}`}
              onClick={() => setConfirmCancel(true)}
              className="ml-auto text-xs font-bold text-red-500 hover:text-red-700 underline"
            >
              Cancel session
            </button>
          )}
        </div>
      )}
    </li>
  );
}
